import { useCallback } from 'react';
import { localeStore } from 'src/mobx/localesStore';
import { IProductInsideCombo } from 'src/types/combo';
import { TTranslate } from 'src/types/main';
import { IProduct, Nutrient, PRODUCT_TYPE } from 'src/types/products';
import { getTranslate } from 'src/utils/formatter';

const units = {
  kcal: 'kcal',
  gram: 'g',
  milligram: 'mg',
};

export const useNutrientModalInfo = (product?: IProduct | IProductInsideCombo) => {
  const isMealComboProduct = (product: IProduct | IProductInsideCombo): product is IProduct =>
    product?.type === PRODUCT_TYPE.MEAL_COMBO;

  /** Renders nutrient values */
  const renderValues = useCallback((values?: Nutrient) => {
    if (!values) {
      return null;
    } else {
      const entries = Object.entries(values)
        .filter(([key]) => !['serving_size', 'serving_unit'].includes(key))
        .map(([key, value]) => {
          const [name, unit] = key.split('_in_');
          return { name, unit, value };
        });

      return entries.map((entry) => {
        return (
          <li key={entry.name} className="d-flex justify-content-between nutrient-li">
            <div>
              <span className="text-size-14 nutrient-text">
                {localeStore.t(`ap_item_nutrients_popup_${entry.name}_field`)}
              </span>
            </div>
            <div>
              <span className="text-size-14 nutrient-text">
                {entry.value} {localeStore.t(units[entry.unit as keyof typeof units])}
              </span>
            </div>
          </li>
        );
      });
    }
  }, []);

  /** Renders title of product */
  const renderTitle = useCallback((name?: TTranslate, servingSize?: number, servingUnit?: 'g' | 'ml' | 'portion') => {
    return (
      <div className="nutrient-title">
        <span className="text-size-18 nutrient-text">{getTranslate(name)}</span>
        <span className="text-size-12 nutrient-subtitle">
          {localeStore.t('ap_item_nutrients_popup_serving_size_field')} {servingSize}{' '}
          {localeStore.t(servingUnit as string)?.toLowerCase()}
        </span>
      </div>
    );
  }, []);

  /** Prepares table of nutrient information */
  const getTable = useCallback(
    (itemsCount: number, itemName?: TTranslate, itemNutrient?: Nutrient, isLastItem?: boolean) => {
      const marginBottom = itemsCount > 1 ? `${isLastItem ? '' : 'mb-4'}` : '';
      return (
        <div className={marginBottom}>
          {renderTitle(itemName, itemNutrient?.serving_size, itemNutrient?.serving_unit)}
          {renderValues(itemNutrient)}
        </div>
      );
    },
    []
  );

  /** Returns nutrient modal information */
  const getInfo = useCallback(() => {
    if (!!product && isMealComboProduct(product)) {
      const itemsWithNutrients = product?.items?.filter((item) => !!item.nutrient);
      return itemsWithNutrients?.map((item, index) => {
        return getTable(
          itemsWithNutrients?.length,
          item?.name,
          item?.nutrient,
          index === itemsWithNutrients.length - 1
        );
      });
    } else {
      return getTable(1, product?.name, product?.nutrient);
    }
  }, [product]);

  return { getInfo };
};
